import { z } from "zod";
// Status code enum - matches Recall.ai API values
export const StatusCodeSchema = z.enum([
    "ready",
    "joining_call",
    "in_waiting_room",
    "participant_in_waiting_room",
    "in_call_not_recording",
    "recording_permission_allowed",
    "recording_permission_denied",
    "in_call_recording",
    "call_ended",
    "recording_done",
    "done",
    "fatal",
    "analysis_done",
    "analysis_failed",
    "media_expired",
    "starting_up",
]);
// Status change schema
export const StatusChangeSchema = z.object({
    code: StatusCodeSchema,
    message: z.union([z.string(), z.null()]).optional(),
    created_at: z.string(),
    sub_code: z.union([z.string(), z.null()]).optional(),
});
// Meeting URL schemas - platform-specific implementations
export const MicrosoftTeamsUrlSchema = z
    .object({
    meeting_id: z.string().optional().nullable(),
    meeting_password: z.string().optional().nullable(),
    organizer_id: z.string().optional().nullable(),
    tenant_id: z.string().optional().nullable(),
    message_id: z.string().optional().nullable(),
    thread_id: z.string().optional().nullable(),
    business_meeting_id: z.string().optional().nullable(),
    business_meeting_password: z.string().optional().nullable(),
    platform: z.literal("microsoft_teams"),
})
    .passthrough();
export const MicrosoftTeamsLiveUrlSchema = z
    .object({
    meeting_id: z.string().optional().nullable(),
    meeting_password: z.string().optional().nullable(),
    organizer_id: z.string().optional().nullable(),
    tenant_id: z.string().optional().nullable(),
    message_id: z.string().optional().nullable(),
    thread_id: z.string().optional().nullable(),
    business_meeting_id: z.string().optional().nullable(),
    business_meeting_password: z.string().optional().nullable(),
    platform: z.literal("microsoft_teams_live"),
})
    .passthrough();
// Meeting URL schema (comprehensive union of all platforms)
export const MeetingUrlSchema = z.union([
    z
        .object({
        meeting_id: z.string(),
        platform: z.literal("google_meet"),
    })
        .passthrough(),
    z
        .object({
        meeting_id: z.string(),
        platform: z.literal("zoom"),
        meeting_password: z.string().optional().nullable(),
        tk: z.string().optional().nullable(),
        zak: z.string().optional().nullable(),
    })
        .passthrough(),
    MicrosoftTeamsUrlSchema,
    MicrosoftTeamsLiveUrlSchema,
]);
// Meeting metadata schema
export const MeetingMetadataSchema = z.object({
    title: z.string(),
    zoom_meeting_uuid: z.string().optional(),
});
// Participant event schema
export const ParticipantEventSchema = z.object({
    code: z.string(),
    created_at: z.string(),
});
// Extra data schemas for different platforms
export const ZoomExtraDataSchema = z.object({
    user_guid: z.string(),
    guest: z.boolean(),
    conf_user_id: z.string(),
});
export const MicrosoftTeamsExtraDataSchema = z.object({
    participant_type: z.string(),
    role: z.string(),
    meeting_role: z.string(),
    user_id: z.string(),
    tenant_id: z.string(),
    client_version: z.string(),
});
// Deprecated: Slack support - kept for API compatibility but not actively supported
export const SlackExtraDataSchema = z.object({
    user_id: z.string(),
    email: z.string().optional(),
    channel_id: z.string().optional(),
});
export const ExtraDataSchema = z.object({
    zoom: ZoomExtraDataSchema.optional(),
    microsoft_teams: MicrosoftTeamsExtraDataSchema.optional(),
    slack: SlackExtraDataSchema.optional(), // Deprecated: kept for API compatibility
});
// Recall.ai meeting participant schema - represents raw data from Recall.ai API
export const RecallMeetingParticipantSchema = z.object({
    id: z.number(),
    name: z.string(),
    platform: z.string(),
    events: z.array(ParticipantEventSchema),
    is_host: z.boolean(),
    extra_data: ExtraDataSchema.optional().nullable(),
    // Note: No 'role' field - that's business logic added by our ParticipantRoleService
});
// Recording schema
export const RecordingSchema = z.object({
    id: z.string(),
    status: z.union([
        z.string(), // Legacy format - simple string status (if it exists)
        z.object({
            // Current API format - status object
            code: z.string(),
            sub_code: z.string().nullable(),
            updated_at: z.string(),
        }),
    ]),
    created_at: z.string().optional(),
    started_at: z.string().optional(),
    completed_at: z.string().nullable().optional(),
    expires_at: z.string().nullable().optional(),
    media_shortcuts: z.record(z.unknown()).optional(),
    metadata: z.record(z.unknown()).optional(),
});
// Utility function to extract status as string from either format
export function getRecordingStatus(recording) {
    if (typeof recording.status === "string") {
        return recording.status;
    }
    // If it's an object, extract the code field
    if (typeof recording.status === "object" && recording.status !== null) {
        return recording.status.code;
    }
    return "unknown";
}
// Recall.ai calendar meeting schema
export const CalendarMeetingSchema = z.object({
    id: z.string(),
    start_time: z.string(),
    end_time: z.string(),
    calendar_user: z.object({
        id: z.string(),
        external_id: z.string(),
    }),
    // Add missing fields from web app
    title: z.string().optional(),
    meetingUrl: z.string().optional(),
});
// Additional schemas needed for comprehensive RecallBot
export const RetentionSchema = z.object({
    days: z.number().optional(),
});
export const RealTimeTranscriptionSchema = z.object({
    destination_url: z.string(),
    partial_results: z.boolean(),
});
export const RealTimeMediaSchema = z.object({
    webhook_call_events_destination_url: z.string().optional(),
    rtmp_destination_url: z.string().optional(),
    websocket_video_destination_url: z.string().optional(),
});
export const TranscriptionOptionsSchema = z.object({
    provider: z.string(),
});
export const AutomaticLeaveSchema = z.object({
    waiting_room_timeout: z.number(),
    noone_joined_timeout: z.number(),
    everyone_left_timeout: z.number(),
});
export const ZoomPlatformSchema = z.object({
    user_email: z.string().optional(),
    require_passcode_for_scheduled_meetings: z.boolean().optional(),
    require_passcode_for_instant_meetings: z.boolean().optional(),
    require_passcode_for_pmi_meetings: z.string().optional(),
    meeting_authentication: z.boolean().optional(),
    authentication_option: z.string().optional(),
    authentication_domains: z.string().optional(),
    additional_data_center_regions: z.array(z.string()).optional(),
    only_host_can_screenshot: z.boolean().optional(),
});
// Comprehensive RecallBot schema - THE SOURCE OF TRUTH
export const RecallBotSchema = z.object({
    id: z.string(),
    video_url: z.string().optional().nullable(),
    media_retention_end: z.string().optional().nullable(),
    status_changes: z.array(StatusChangeSchema).optional().nullable(),
    meeting_metadata: MeetingMetadataSchema.optional().nullable(),
    meeting_participants: z
        .array(RecallMeetingParticipantSchema)
        .optional()
        .nullable(),
    meeting_url: MeetingUrlSchema.optional().nullable(),
    metadata: z.record(z.unknown()).optional().nullable(),
    join_at: z.string().optional().nullable(),
    calendar_meetings: z.array(CalendarMeetingSchema).optional().nullable(),
    recording: z.string().optional().nullable(),
    recordings: z.array(RecordingSchema).optional().nullable(),
    retention: RetentionSchema.optional().nullable(),
    bot_name: z.string().optional().nullable(),
    real_time_transcription: RealTimeTranscriptionSchema.optional().nullable(),
    real_time_media: RealTimeMediaSchema.optional().nullable(),
    transcription_options: TranscriptionOptionsSchema.optional().nullable(),
    recording_mode: z.string().optional().nullable(),
    automatic_leave: AutomaticLeaveSchema.optional().nullable(),
    zoom: ZoomPlatformSchema.optional().nullable(),
});
// Response schemas
export const GetBotResponseSchema = z.object({
    count: z.number(),
    next: z.string().nullable(),
    previous: z.string().nullable(),
    results: z.array(RecallBotSchema),
});
export const CreateBotResponseSchema = RecallBotSchema.extend({
    status_changes: z.literal(true),
});
// Re-export StatusCode for convenience (already exported above)
// export type StatusCode is already exported above
